import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/formatDate";
import { Facebook, Lock, ChevronDown } from "lucide-react";

const Row = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-3 py-0.5">
      <dt className="text-[color:var(--sp-text-muted)]">{label}</dt>
      <dd className="font-bold text-right break-all">{value}</dd>
    </div>
  ) : null;

/** Sezione "Acquisizione" READ-ONLY: dati tecnici creati dal sistema, non
 *  modificabili dall'utente. Il tenant vede il riepilogo; l'agenzia il dettaglio. */
export const ClientAcquisition = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/clients/${clientId}/acquisitions`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ acquisitions: [] }); });
    return () => { alive = false; };
  }, [clientId]);

  const list = data?.acquisitions || [];
  if (list.length === 0) return null;
  const main = list[0];
  const detailed = data?.detailed;

  return (
    <div data-testid="client-acquisition-card" className="sp-card p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-[11px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] inline-flex items-center gap-1.5">
          <Facebook className="h-3.5 w-3.5" /> Acquisizione
        </h3>
        <span className="inline-flex items-center gap-1 text-[10px] text-[color:var(--sp-text-muted)]">
          <Lock className="h-3 w-3" /> dato di sistema
        </span>
      </div>
      <dl className="text-xs space-y-0.5" data-testid="acquisition-main">
        <Row label="Origine" value={main.source_label} />
        <Row label="Tipo" value={main.source_type_label} />
        <Row label="Acquisito il" value={main.acquired_at ? fmtDateTime(main.acquired_at) : null} />
        <Row label="Landing" value={main.landing_name} />
        <Row label="Form" value={main.form_name} />
        <Row label="Campagna" value={main.campaign_name} />
        <Row label="UTM Campaign" value={main.utm_campaign} />
        <Row label="UTM Content" value={main.utm_content} />
        {detailed && (
          <>
            <Row label="Ad Set" value={main.adset_name} />
            <Row label="Annuncio" value={main.ad_name} />
            <Row label="Campaign ID" value={main.campaign_id} />
            <Row label="Ad Set ID" value={main.adset_id} />
            <Row label="Ad ID" value={main.ad_id} />
            <Row label="Creative ID" value={main.creative_id} />
            <Row label="fbclid" value={main.tracking?.fbclid} />
            <Row label="Landing URL" value={main.landing_url} />
            <Row label="Lead ID esterno" value={main.external_lead_id || main.leadgen_id} />
          </>
        )}
      </dl>

      {detailed && Object.keys(main.answers || {}).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[color:var(--sp-border)]">
          <p className="text-[10px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-1">Risposte del form</p>
          <dl className="text-xs space-y-0.5" data-testid="acquisition-answers">
            {Object.entries(main.answers).map(([k, v]) => <Row key={k} label={k} value={v} />)}
          </dl>
        </div>
      )}

      {list.length > 1 && (
        <div className="mt-3 pt-3 border-t border-[color:var(--sp-border)]">
          <button data-testid="acquisition-history-toggle" onClick={() => setShowAll((v) => !v)}
            className="text-xs font-bold text-[color:var(--sp-brand)] inline-flex items-center gap-1">
            <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
            Storico acquisizioni ({list.length})
          </button>
          {showAll && (
            <ul data-testid="acquisition-history" className="mt-2 space-y-1.5 text-xs">
              {list.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-2">
                  <span className="text-[color:var(--sp-text-muted)]">
                    {a.acquired_at ? fmtDateTime(a.acquired_at) : "—"}
                  </span>
                  <span className="text-right font-medium">
                    {a.source_label} → {a.source_type_label}
                    {(a.landing_name || a.form_name) && ` · ${a.landing_name || a.form_name}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
